<template>
  <div class="p-4 sm:p-6">
    <BaseVeeForm :action="saveEmailMessage">
      <template #default="formProps">
        <div class="space-y-8">
          <table class="table-auto w-full">
            <tbody>
              <tr>
                <td class="whitespace-nowrap py-4 pr-3 w-0 align-top">
                  <BaseFormLabel> From </BaseFormLabel>
                </td>
                <td class="whitespace-nowrap py-2 pl-3">
                  <BaseFormSelectFieldWithValidator
                    v-model="emailMessageData.third_party_service_id"
                    v-bind="fromFieldProps"
                    :disabled="formProps.isPending"
                  />
                </td>
              </tr>
              <tr>
                <td class="whitespace-nowrap py-4 pr-3 w-0 align-top">
                  <BaseFormLabel> To </BaseFormLabel>
                </td>
                <td class="whitespace-nowrap py-2 pl-3">
                  <ToSelectField
                    v-if="!contactRequest"
                    :disabled="formProps.isPending"
                    v-model="emailMessageData.to_address"
                    :options="toOptions"
                  />
                  <span v-else>{{ emailMessageData.to_address }}</span>
                </td>
              </tr>
              <tr>
                <td class="whitespace-nowrap py-4 pr-3 w-0 align-top">
                  <BaseFormLabel> Subject </BaseFormLabel>
                </td>
                <td class="whitespace-nowrap py-2 pl-3">
                  <BaseFormTextFieldWithValidator
                    v-model="emailMessageData.subject"
                    v-bind="subjectFieldProps"
                    :disabled="formProps.isPending"
                    class="w-lg"
                  />
                </td>
              </tr>
              <tr>
                <td class="whitespace-nowrap py-4 pr-3 align-top">
                  <BaseFormLabel> Template </BaseFormLabel>
                </td>
                <td class="whitespace-nowrap py-2 pl-3 flex gap-4 items-start">
                  <div class="flex-1">
                    <EmailTemplateSelectField
                      :account-id="contact.account_id"
                      :disabled="formProps.isPending"
                      v-model="emailTemplateValue"
                    />
                  </div>
                  <div v-if="!!emailMessageData.email_template_id" class="flex-auto">
                    <BaseButton
                      type="button"
                      primary
                      outline
                      @click="
                        () => {
                          return formProps.submit().then(() => {
                            $emit('edit-template');
                          });
                        }
                      "
                    >
                      Edit template
                    </BaseButton>
                  </div>
                </td>
              </tr>
              <tr v-if="acceptsUserContent">
                <td class="whitespace-nowrap py-4 pr-3 align-top">
                  <BaseFormLabel> Content </BaseFormLabel>
                </td>
                <td class="whitespace-nowrap py-2 pl-3 space-y-2">
                  <ckeditor
                    ref="ckeditor"
                    v-model="emailMessageData.content"
                    :config="editorConfig"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div class="flex justify-end gap-4">
            <BaseButton primary outline :disabled="formProps.isPending"> Save </BaseButton>

            <BaseButton
              type="button"
              primary
              :disabled="formProps.isPending"
              @click="
                () => {
                  return formProps.submit().then(() => {
                    $emit('preview');
                  });
                }
              "
            >
              Preview
            </BaseButton>
          </div>
        </div>
      </template>
    </BaseVeeForm>
  </div>
</template>

<script>
import { accountThirdPartyServicesClient, contactDraftEmailMessagesClient } from '@/api/clients';
import EmailTemplateSelectField from './EmailTemplateSelectField.vue';
import ToSelectField from './ToSelectField.vue';

const editorConfig = {
  toolbarGroups: [
    { name: 'clipboard', groups: ['clipboard', 'undo'] },
    { name: 'editing', groups: ['find', 'selection', 'spellchecker', 'editing'] },
    { name: 'links', groups: ['links'] },
    { name: 'insert', groups: ['insert'] },
    { name: 'forms', groups: ['forms'] },
    { name: 'tools', groups: ['tools'] },
    { name: 'document', groups: ['mode', 'document', 'doctools'] },
    { name: 'others', groups: ['others'] },
    '/',
    { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
    { name: 'paragraph', groups: ['list', 'indent', 'blocks', 'align', 'bidi', 'paragraph'] },
    { name: 'styles', groups: ['styles'] },
    { name: 'colors', groups: ['colors'] },
    { name: 'about', groups: ['about'] },
  ],
  removeButtons: 'Subscript,Superscript,About,Image,Anchor,Source,Styles',
};

const defaultEmailMessageData = {
  third_party_service_id: null,
  to_address: '',
  subject: '',
  content: '',
  content_html: null,
  content_plain: null,
  contact_request_id: null,
  email_template_id: null,
};

const getDefaultEmailMessageData = (contact, contactRequest = null) => {
  const emailMessageData = { ...defaultEmailMessageData };

  if (contactRequest) {
    emailMessageData.to_address = contactRequest.email || null;
    emailMessageData.contact_request_id = contactRequest.id;
  } else {
    emailMessageData.to_address = contact?.primary_email_address?.address || null;
  }

  emailMessageData.subject = '';
  emailMessageData.content = '';
  emailMessageData.email_template_id = null;

  return emailMessageData;
};

const getEmailMessageDataFromEmailMessage = (emailMessage) => {
  const emailMessageData = { ...defaultEmailMessageData };

  emailMessageData.third_party_service_id = emailMessage?.third_party_service_id || null;
  emailMessageData.to_address = emailMessage.to_address;
  emailMessageData.contact_request_id = emailMessage.contact_request_id;
  emailMessageData.subject = emailMessage.subject || '';
  emailMessageData.content = emailMessage.content || '';
  emailMessageData.content_html = emailMessage.content_html || null;
  emailMessageData.content_plain = emailMessage.content_plain || null;
  emailMessageData.email_template_id = emailMessage.email_template_id || null;

  return emailMessageData;
};

const mapThirdPartyServicesToFromOptions = (thirdPartyServices) =>
  thirdPartyServices.map((thirdPartyService) => ({
    value: thirdPartyService.id,
    label: thirdPartyService.email_address,
    signature: thirdPartyService.signature || '',
  }));

export default {
  components: {
    EmailTemplateSelectField,
    ToSelectField,
  },
  props: {
    contact: {
      type: Object,
      required: true,
    },
    contactRequest: {
      type: Object,
      default: () => null,
    },
    emailMessageId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      fromOptions: [],
      editorConfig,
      emailMessageData: getDefaultEmailMessageData(this.contact, this.contactRequest),
    };
  },
  computed: {
    emailTemplateValue: {
      get() {
        return this.emailMessageData.email_template_id;
      },
      set(emailTemplate) {
        this.emailMessageData.email_template_id = emailTemplate.id;
        this.emailMessageData.content_html = emailTemplate.content_html;
        this.emailMessageData.content_plain = emailTemplate.content_plain;
      },
    },
    toOptions() {
      return (this.contact?.email_addresses || []).map((emailAddress) => emailAddress.address);
    },
    fromFieldProps() {
      return {
        validator: {
          vid: 'third_party_service_id',
          name: 'from',
          rules: { required: false },
        },
        field: {
          id: 'third_party_service_id',
          options: this.fromOptions,
        },
      };
    },
    subjectFieldProps() {
      return {
        validator: {
          vid: 'subject',
          name: 'subject',
          rules: { required: false },
        },
        field: {
          id: 'subject',
        },
      };
    },
    acceptsUserContent() {
      if (typeof this.emailMessageData.content_html !== 'string') {
        return false;
      }

      return this.emailMessageData.content_html.includes('{{content}}');
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      try {
        await Promise.all([
          this.loadFromOptions(),
          ...(this.emailMessageId ? [this.loadEmailMessage()] : []),
        ]);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async loadEmailMessage() {
      try {
        const response = await contactDraftEmailMessagesClient.read(
          this.contact.id,
          this.emailMessageId
        );

        this.emailMessageData = getEmailMessageDataFromEmailMessage(response.data);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async loadFromOptions() {
      try {
        const response = await accountThirdPartyServicesClient.list(this.contact.account_id, {
          params: {
            filter: {
              channel: 'mail',
            },
          },
        });

        this.fromOptions = mapThirdPartyServicesToFromOptions(response.data.data);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async saveEmailMessage() {
      try {
        const response = this.emailMessageId
          ? await this.updateEmailMessage()
          : await this.storeEmailMessage();

        this.emailMessageData = getEmailMessageDataFromEmailMessage(response.data);

        this.$emit('saved', response.data);
      } catch (error) {
        // handle error
      }
    },
    storeEmailMessage() {
      return contactDraftEmailMessagesClient.store(this.contact.id, {
        ...this.emailMessageData,
      });
    },
    updateEmailMessage() {
      return contactDraftEmailMessagesClient.update(this.contact.id, this.emailMessageId, {
        ...this.emailMessageData,
      });
    },
  },
};
</script>
